import useUrlState from "@ahooksjs/use-url-state";
import type { VisibilityFilter as VisibilityFilterType } from "@albertine/types";
import {
    MembershipStageId,
    MembershipTierId,
    OfferingCategory,
    SortOrderDto,
    Tag,
    type TasksQueryFilterDto,
    TasksSortFields,
    VisibilityFilter,
} from "@albertine/types";
import {
    type SearchCatalogItemsQuery,
    SearchCatalogItemsQuerySchema,
    type Tenant,
    TenantSchema,
} from "@albertine/types/openapi/lmt";
import { ThemeLocation, ThemePreference } from "@albertine/types/theme";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouteMatch } from "react-router";
import { z } from "zod";
import type { CatalogQueryFilters } from "../common/CatalogItem";
import {
    isRequestDeadlineStatusArray,
    type RequestDeadlineStatus,
} from "../common/types/Deadline";
import {
    type TenantPointOfView,
    TenantPointOfViewSchema,
    type ThemeQueryFilters,
} from "../common/types/DynamicTheme";
import { EngagementLevel } from "../common/types/Engagement";
import {
    MemberGroupKey,
    RequestGroupKey,
    TasksGroupKey,
} from "../common/types/FilterGroups";
import { GroupId } from "../common/types/Group";
import { isMentionStatusArray, MentionStatus } from "../common/types/Mention";
import { CategoriesWithUncategorized } from "../common/types/OfferingCategory";
import {
    isSortBy,
    isSortOrder,
    type SortBy,
    type SortOrder,
} from "../common/types/Sort";
import { isString, stringToArray } from "../common/utils/string.util";
import { isTypeOf } from "../common/utils/zod.util";
import { MemberId } from "../common/validate";
import { AgentEngagementLevel } from "../components/MembersList/filters/AgentEngagementFilters";
import { RequestFilter } from "../components/MembersList/filters/RequestFilters";
import { CatalogContext, routeSchemas } from "./routes";

export function useDebounce<T>(value: T, delay?: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedValue(value);
        }, delay || 400);

        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
}

type AnyUrlState = {
    sortBy: unknown;
    sortOrder: unknown;
    assignedTo: unknown[];
    createdTo: unknown[];
    mentionStatusList: unknown[];
    statusList: unknown[];
    group: unknown[];
    engagement: unknown[];
    request: unknown[];
    agentEngagement: unknown[];
    membershipTier: unknown[];
    membershipStage: unknown[];
    requestCategory: unknown[];
    teamIds: unknown[];
};

export type UrlState = {
    sortBy: SortBy | undefined;
    sortOrder: SortOrder | undefined;
    assignedTo: string[] | undefined;
    createdTo: string[] | undefined;
    mentionStatusList: MentionStatus[] | undefined;
    statusList: RequestDeadlineStatus[] | undefined;
    group: GroupId[] | undefined;
    engagement: EngagementLevel[] | undefined;
    request: RequestFilter[] | undefined;
    agentEngagement: AgentEngagementLevel[] | undefined;
    membershipTier: MembershipTierId[] | undefined;
    membershipStage: MembershipStageId[] | undefined;
    requestCategory: CategoriesWithUncategorized[] | undefined;
    teamIds: string[] | undefined;
};

export const useUrlParams = () => {
    const [urlState, setUrlState] = useUrlState<AnyUrlState>();
    const {
        createdTo,
        sortBy,
        sortOrder,
        assignedTo,
        statusList,
        mentionStatusList,
        group,
        engagement,
        request,
        agentEngagement,
        membershipTier,
        membershipStage,
        requestCategory,
        teamIds,
    } = urlState;
    const assignedToArray = stringToArray(assignedTo);
    const createdToArray = stringToArray(createdTo);
    const mentionStatusListArray = stringToArray(mentionStatusList);
    const statusListArray = stringToArray(statusList);
    const groupArray = stringToArray(group);
    const engagementArray = stringToArray(engagement);
    const requestArray = stringToArray(request);
    const agentEngagementArray = stringToArray(agentEngagement);
    const membershipTierArray = stringToArray(membershipTier);
    const membershipStageArray = stringToArray(membershipStage);
    const requestCategoryArray = stringToArray(requestCategory);
    const teamIdsArray = stringToArray(teamIds);

    const parsedUrlState: UrlState = useMemo(
        () => ({
            sortBy: isSortBy(sortBy) ? sortBy : undefined,
            sortOrder: isSortOrder(sortOrder) ? sortOrder : undefined,
            assignedTo: assignedToArray.length !== 0 ? assignedToArray : undefined,
            createdTo: createdToArray.length !== 0 ? createdToArray : undefined,
            mentionStatusList:
                isMentionStatusArray(mentionStatusListArray) &&
                mentionStatusListArray.length !== 0
                    ? mentionStatusListArray
                    : undefined,
            statusList:
                isRequestDeadlineStatusArray(statusListArray) &&
                statusListArray.length !== 0
                    ? statusListArray
                    : undefined,
            group:
                isTypeOf(groupArray, z.array(GroupId)) && groupArray.length !== 0
                    ? groupArray
                    : undefined,
            engagement:
                isTypeOf(engagementArray, z.array(EngagementLevel)) &&
                engagementArray.length !== 0
                    ? engagementArray
                    : undefined,
            request:
                isTypeOf(requestArray, z.array(RequestFilter)) &&
                requestArray.length !== 0
                    ? requestArray
                    : undefined,
            agentEngagement:
                isTypeOf(agentEngagementArray, z.array(AgentEngagementLevel)) &&
                agentEngagementArray.length !== 0
                    ? agentEngagementArray
                    : undefined,
            membershipTier:
                isTypeOf(membershipTierArray, z.array(MembershipTierId)) &&
                membershipTierArray.length !== 0
                    ? membershipTierArray
                    : undefined,
            membershipStage:
                isTypeOf(membershipStageArray, z.array(MembershipStageId)) &&
                membershipStageArray.length !== 0
                    ? membershipStageArray
                    : undefined,
            requestCategory:
                isTypeOf(requestCategoryArray, z.array(CategoriesWithUncategorized)) &&
                requestCategoryArray.length !== 0
                    ? requestCategoryArray
                    : undefined,
            teamIds: teamIdsArray.length !== 0 ? teamIdsArray : undefined,
        }),
        [
            sortBy,
            sortOrder,
            agentEngagementArray,
            assignedToArray,
            createdToArray,
            mentionStatusListArray,
            statusListArray,
            groupArray,
            engagementArray,
            requestArray,
            membershipTierArray,
            membershipStageArray,
            requestCategoryArray,
            teamIdsArray,
        ],
    );

    return useMemo(
        () => ({
            urlState: parsedUrlState,
            setUrlState,
        }),
        [parsedUrlState, setUrlState],
    );
};

type AnyCatalogUrlState = {
    searchTerm: unknown;
    nameSearchTerm: unknown;
    locationSearchTerm: unknown;
    offeringCategories: unknown;
    recommendedItemIds: unknown;
    viewAiRecommendations: unknown;
    suppliers: unknown;
    country: unknown;
    published: unknown;
    source: unknown;
    tags: unknown;
    albertineVerified: unknown;
    recentlyOpened: unknown;
    missingTranslations: unknown;
    discoverableForTenant: unknown;
    lat: unknown;
    lon: unknown;
    view: unknown;
    bounds: unknown;
    maxGeoDistanceKm: unknown;
    ids: unknown;
};

const CatalogSearchUrlStateSchema = z.object({
    filter: SearchCatalogItemsQuerySchema,
    recommendedItemIds: z.array(z.string()).optional(),
    viewAiRecommendations: z.literal(true).optional(),
    locationSearchTerm: z.string().optional(),
    view: z.enum(["list", "map"]),
});
export type CatalogSearchUrlState = z.infer<typeof CatalogSearchUrlStateSchema>;

export function useCatalogSearchUrlParams() {
    const [urlState, setUrlState] = useUrlState<AnyCatalogUrlState>(undefined, {
        parseOptions: { arrayFormat: "index" },
        stringifyOptions: { arrayFormat: "index" },
    });

    const {
        searchTerm,
        nameSearchTerm,
        locationSearchTerm,
        offeringCategories,
        recommendedItemIds,
        viewAiRecommendations,
        suppliers,
        country,
        published,
        source,
        tags,
        albertineVerified,
        recentlyOpened,
        missingTranslations,
        discoverableForTenant,
        lat,
        lon,
        view,
        bounds,
        maxGeoDistanceKm,
        ids,
    } = urlState;

    const parsedUrlState: CatalogSearchUrlState = useMemo(() => {
        const boundsParsed = isString(bounds) ? JSON.parse(bounds) : {};
        const { top, bottom, left, right } = boundsParsed;
        const geoBoundingBoxTop =
            top !== undefined ? Number.parseFloat(top) : undefined;
        const geoBoundingBoxBottom =
            bottom !== undefined ? Number.parseFloat(bottom) : undefined;
        const geoBoundingBoxLeft =
            left !== undefined ? Number.parseFloat(left) : undefined;
        const geoBoundingBoxRight =
            right !== undefined ? Number.parseFloat(right) : undefined;
        const hasAllGeoParams =
            geoBoundingBoxTop !== undefined &&
            geoBoundingBoxBottom !== undefined &&
            geoBoundingBoxLeft !== undefined &&
            geoBoundingBoxRight !== undefined;
        const geoFilter: SearchCatalogItemsQuery = !hasAllGeoParams
            ? {}
            : {
                  geoBoundingBoxTop,
                  geoBoundingBoxBottom,
                  geoBoundingBoxLeft,
                  geoBoundingBoxRight,
              };

        return {
            filter: {
                ...geoFilter,
                searchTerm: isString(searchTerm) ? searchTerm : undefined,
                nameSearchTerm: isString(nameSearchTerm) ? nameSearchTerm : undefined,
                categories: isTypeOf(
                    offeringCategories,
                    SearchCatalogItemsQuerySchema.shape.categories,
                )
                    ? offeringCategories
                    : undefined,

                supplierLmtIds: isTypeOf(
                    suppliers,
                    SearchCatalogItemsQuerySchema.shape.supplierLmtIds,
                )
                    ? suppliers
                    : undefined,
                published: isTypeOf(
                    published,
                    SearchCatalogItemsQuerySchema.shape.published,
                )
                    ? published
                    : undefined,
                sources: isTypeOf(source, SearchCatalogItemsQuerySchema.shape.sources)
                    ? source
                    : undefined,
                albertineVerified: isTypeOf(
                    albertineVerified,
                    SearchCatalogItemsQuerySchema.shape.albertineVerified,
                )
                    ? albertineVerified
                    : undefined,
                recentlyOpened: isTypeOf(
                    recentlyOpened,
                    SearchCatalogItemsQuerySchema.shape.recentlyOpened,
                )
                    ? recentlyOpened
                    : undefined,
                missingTranslations: isTypeOf(
                    missingTranslations,
                    SearchCatalogItemsQuerySchema.shape.missingTranslations,
                )
                    ? missingTranslations
                    : undefined,
                discoverableForTenant: isTypeOf(
                    discoverableForTenant,
                    TenantSchema.or(z.literal("all")),
                )
                    ? discoverableForTenant
                    : undefined,
                tags: isTypeOf(tags, SearchCatalogItemsQuerySchema.shape.tags)
                    ? tags
                    : undefined,
                country: isString(country) ? country : undefined,
                latitude: lat !== undefined ? Number.parseFloat(lat) : undefined,
                longitude: lon !== undefined ? Number.parseFloat(lon) : undefined,
                maxGeoDistanceKm: isTypeOf(
                    maxGeoDistanceKm,
                    SearchCatalogItemsQuerySchema.shape.maxGeoDistanceKm,
                )
                    ? maxGeoDistanceKm
                    : undefined,
                ids: isTypeOf(ids, SearchCatalogItemsQuerySchema.shape.ids)
                    ? ids
                    : undefined,
            },
            recommendedItemIds: isTypeOf(
                recommendedItemIds,
                CatalogSearchUrlStateSchema.shape.recommendedItemIds,
            )
                ? recommendedItemIds
                : undefined,
            viewAiRecommendations:
                viewAiRecommendations === "1" || viewAiRecommendations === true
                    ? true
                    : undefined,
            locationSearchTerm: isString(locationSearchTerm)
                ? locationSearchTerm
                : undefined,
            view: view === "map" ? "map" : "list",
        };
    }, [
        searchTerm,
        nameSearchTerm,
        locationSearchTerm,
        offeringCategories,
        suppliers,
        published,
        source,
        tags,
        albertineVerified,
        recentlyOpened,
        missingTranslations,
        discoverableForTenant,
        lat,
        lon,
        view,
        bounds,
        maxGeoDistanceKm,
        ids,
        country,
        recommendedItemIds,
        viewAiRecommendations,
    ]);

    const setMappedState = useCallback(
        (partialUrlState: Partial<CatalogSearchUrlState>) => {
            const mappedState =
                catalogSearchUrlStateToAnyCatalogUrlState(partialUrlState);
            setUrlState(mappedState);
        },
        [setUrlState],
    );

    return {
        urlState: parsedUrlState,
        setUrlState: setMappedState,
    };
}

function catalogSearchUrlStateToAnyCatalogUrlState(
    partialUrlState: Partial<CatalogSearchUrlState>,
): Partial<AnyCatalogUrlState> {
    const filter = partialUrlState.filter ?? {};
    const hasAnyGeoParam =
        filter.geoBoundingBoxTop !== undefined ||
        filter.geoBoundingBoxBottom !== undefined ||
        filter.geoBoundingBoxLeft !== undefined ||
        filter.geoBoundingBoxRight !== undefined;

    return {
        searchTerm: filter.searchTerm,
        nameSearchTerm: filter.nameSearchTerm,
        locationSearchTerm: partialUrlState.locationSearchTerm,
        offeringCategories: filter.categories,
        recommendedItemIds: partialUrlState.recommendedItemIds,
        viewAiRecommendations: partialUrlState.viewAiRecommendations ? "1" : undefined,
        suppliers: filter.supplierLmtIds,
        source: filter.sources,
        published: filter.published,
        country: filter.country,
        tags: filter.tags,
        albertineVerified: filter.albertineVerified,
        recentlyOpened: filter.recentlyOpened,
        missingTranslations: filter.missingTranslations,
        discoverableForTenant: filter.discoverableForTenant,
        lat: filter.latitude,
        lon: filter.longitude,
        view: partialUrlState.view,
        bounds: hasAnyGeoParam
            ? JSON.stringify({
                  top: filter.geoBoundingBoxTop,
                  bottom: filter.geoBoundingBoxBottom,
                  left: filter.geoBoundingBoxLeft,
                  right: filter.geoBoundingBoxRight,
              })
            : undefined,
        maxGeoDistanceKm: filter.maxGeoDistanceKm,
        ids: filter.ids,
    };
}

type AnyCatalogSelectionUrlState = {
    catalogId: unknown;
};

export type ParsedCatalogSelectionUrlState = {
    searchCatalogId: string | undefined;
    modeCatalogId: string | undefined;
    mode: "edit" | "create" | "view" | undefined;
    context: CatalogContext;
};

export const useCatalogSelectionUrlParams = () => {
    const matchCatalog = useRouteMatch<{
        catalogId: string;
        mode: "edit" | "create" | "view";
        context: CatalogContext;
    }>(routeSchemas.catalog);

    const matchRequestCatalog = useRouteMatch<{
        catalogId: string;
        mode: "edit" | "create" | "view";
        context: CatalogContext;
    }>(routeSchemas.requestCatalog);

    const matchMemberCatalog = useRouteMatch<{
        catalogId: string;
        mode: "edit" | "create" | "view";
        context: CatalogContext;
    }>(routeSchemas.memberCatalog);

    const [urlState, setUrlState] = useUrlState<AnyCatalogSelectionUrlState>();

    const parsedUrlState: ParsedCatalogSelectionUrlState = useMemo(() => {
        const standaloneCatalogMode = isString(matchCatalog?.params.mode)
            ? matchCatalog?.params.mode
            : undefined;
        const requestCatalogMode = isString(matchRequestCatalog?.params.mode)
            ? matchRequestCatalog?.params.mode
            : undefined;

        const memberCatalogMode = isString(matchMemberCatalog?.params.mode)
            ? matchMemberCatalog?.params.mode
            : undefined;

        const standaloneCatalogId = isString(matchCatalog?.params.catalogId)
            ? matchCatalog?.params.catalogId
            : undefined;

        const requestCatalogId = isString(matchRequestCatalog?.params.catalogId)
            ? matchRequestCatalog?.params.catalogId
            : undefined;

        const memberCatalogId = isString(matchMemberCatalog?.params.catalogId)
            ? matchMemberCatalog?.params.catalogId
            : undefined;

        const standaloneContext: CatalogContext | undefined = isTypeOf(
            matchCatalog?.params.context,
            CatalogContext,
        )
            ? matchCatalog?.params.context
            : undefined;

        const requestContext: CatalogContext | undefined = isTypeOf(
            matchRequestCatalog?.params.context,
            CatalogContext,
        )
            ? matchRequestCatalog?.params.context
            : undefined;

        const memberContext: CatalogContext | undefined = isTypeOf(
            matchMemberCatalog?.params.context,
            CatalogContext,
        )
            ? matchMemberCatalog?.params.context
            : undefined;
        return {
            searchCatalogId: isString(urlState.catalogId)
                ? urlState.catalogId
                : undefined,
            modeCatalogId: standaloneCatalogId || requestCatalogId || memberCatalogId,
            mode: standaloneCatalogMode || requestCatalogMode || memberCatalogMode,
            context: standaloneContext || requestContext || memberContext || "browse",
        };
    }, [urlState.catalogId, matchCatalog, matchRequestCatalog, matchMemberCatalog]);

    return useMemo(
        () => ({
            urlState: parsedUrlState,
            setUrlState,
        }),
        [parsedUrlState, setUrlState],
    );
};

type AnyThemesUrlState = {
    articleId: unknown;
    themeId: unknown;
};

export type ParsedThemeUrlState = {
    articleId: string | undefined;
    themeId: string | undefined;
    mode: "edit" | "create" | "view" | "preview" | undefined;
};

export const useThemesUrlPath = () => {
    const matchTheme = useRouteMatch<{
        themeId: string | undefined;
        mode: "edit" | "create" | "view";
    }>(routeSchemas.themesMode);

    const matchThemeAndArticle = useRouteMatch<{
        themeId: string | undefined;
        articleId: string | undefined;
        mode: "edit" | "create" | "view";
    }>(routeSchemas.themesViewArticle);

    const matchThemeMember = useRouteMatch<{
        memberId: string | undefined;
    }>(routeSchemas.themesPreview);

    const [setUrlState] = useUrlState<AnyThemesUrlState>();

    const parsedUrlState: ParsedThemeUrlState = useMemo(() => {
        if (matchThemeMember?.params.memberId) {
            return {
                articleId: undefined,
                themeId: undefined,
                mode: "preview",
            };
        }

        const matchThemeId = isString(matchTheme?.params.themeId)
            ? matchTheme?.params.themeId
            : undefined;

        const matchThemeId2 = isString(matchThemeAndArticle?.params.themeId)
            ? matchThemeAndArticle?.params.themeId
            : undefined;

        const matchMode = isString(matchTheme?.params.mode)
            ? matchTheme?.params.mode
            : undefined;

        const matchMode2 = isString(matchThemeAndArticle?.params.mode)
            ? matchThemeAndArticle?.params.mode
            : undefined;

        const matchArticleId = isString(matchThemeAndArticle?.params.articleId)
            ? matchThemeAndArticle?.params.articleId
            : undefined;

        return {
            memberId: undefined,
            articleId: matchArticleId,
            themeId: matchThemeId || matchThemeId2,
            mode: matchMode || matchMode2,
        };
    }, [matchTheme, matchThemeAndArticle, matchThemeMember?.params.memberId]);

    return useMemo(
        () => ({
            urlState: parsedUrlState,
            setUrlState,
        }),
        [parsedUrlState, setUrlState],
    );
};

type AnyTasksUrlState = {
    assignedTo: unknown;
    mentionStatus: unknown;
    sortBy: unknown;
    sortOrder: unknown;
};

export const useTasksUrlParams = () => {
    const [urlState, setUrlState] = useUrlState<AnyTasksUrlState>(undefined, {
        parseOptions: { arrayFormat: "index" },
        stringifyOptions: { arrayFormat: "index" },
    });

    const { assignedTo, mentionStatus, sortBy, sortOrder } = urlState;

    const parsedUrlState: TasksQueryFilterDto = useMemo(
        () => ({
            assignedToAgentIds:
                isTypeOf(assignedTo, z.array(z.string())) && assignedTo.length !== 0
                    ? assignedTo
                    : undefined,
            mentionStatus: isTypeOf(mentionStatus, z.array(MentionStatus))
                ? mentionStatus
                : undefined,
            sort:
                isTypeOf(sortBy, TasksSortFields) && isTypeOf(sortOrder, SortOrderDto)
                    ? {
                          by: sortBy,
                          order: sortOrder,
                      }
                    : {
                          by: "mentionCreatedAt",
                          order: "desc",
                      },
        }),

        [assignedTo, mentionStatus, sortBy, sortOrder],
    );

    return useMemo(
        () => ({
            urlState: parsedUrlState,
            setUrlState,
        }),
        [parsedUrlState, setUrlState],
    );
};

type MessagesQueryFilter = {
    assignedToAgentIds?: string[];
    teamIds?: string[];
};

type AnyMessagesUrlState = {
    assignedTo: unknown;
    teamIds: unknown;
};

export const useMessagesUrlParams = () => {
    const [urlState, setUrlState] = useUrlState<AnyMessagesUrlState>(undefined, {
        parseOptions: { arrayFormat: "index" },
        stringifyOptions: { arrayFormat: "index" },
    });

    const { assignedTo, teamIds } = urlState;

    const parsedUrlState: MessagesQueryFilter = {
        assignedToAgentIds:
            isTypeOf(assignedTo, z.array(z.string())) && assignedTo.length !== 0
                ? assignedTo
                : undefined,
        teamIds:
            isTypeOf(teamIds, z.array(z.string())) && teamIds.length !== 0
                ? teamIds
                : undefined,
    };

    return {
        urlState: parsedUrlState,
        setUrlState,
    };
};

export const useRequestsUrlFilter = () => {
    const matchRequestsFilter = useRouteMatch<{ filter: string }>("/requests/:filter")
        ?.params.filter;

    const parsedFilter: RequestGroupKey | undefined = useMemo(
        () =>
            isTypeOf(matchRequestsFilter, RequestGroupKey)
                ? matchRequestsFilter
                : undefined,
        [matchRequestsFilter],
    );

    return parsedFilter;
};

export const useMembersUrlFilter = () => {
    const matchMemberFilter = useRouteMatch<{ filter: string }>("/members/:filter")
        ?.params.filter;

    const parsedFilter: MemberGroupKey | undefined = useMemo(
        () =>
            isTypeOf(matchMemberFilter, MemberGroupKey) ? matchMemberFilter : undefined,
        [matchMemberFilter],
    );

    return parsedFilter;
};

export const useTasksUrlFilter = () => {
    const matchTaskFilter = useRouteMatch<{ filter: string }>("/tasks/:filter")?.params
        .filter;

    const matchTaskRenewedFilter = useRouteMatch<{ filter: string }>(
        "/tasks-renewed/:filter",
    )?.params.filter;

    const parsedFilter: TasksGroupKey | undefined = useMemo(() => {
        if (isTypeOf(matchTaskFilter, TasksGroupKey)) {
            return matchTaskFilter;
        }

        return isTypeOf(matchTaskRenewedFilter, TasksGroupKey)
            ? matchTaskRenewedFilter
            : undefined;
    }, [matchTaskFilter, matchTaskRenewedFilter]);

    return parsedFilter;
};

export function useComponentState<T>(initialState: T) {
    const [componentState, setComponentState] = useState<T>(initialState);

    const setState = (partialState: Partial<typeof componentState>) => {
        setComponentState((prevState) => ({
            ...prevState,
            ...partialState,
        }));
    };

    return { state: componentState, setState };
}

type AnyThemesUrlQueryState = {
    name: unknown;
    city: unknown;
    country: unknown;
    countryCode: unknown;
    preferences: unknown;
    published: VisibilityFilterType;
    tenantPointOfView: TenantPointOfView;
};

export const useThemesUrlQuery = () => {
    const [urlState, setUrlState] = useUrlState<AnyThemesUrlQueryState>(undefined, {
        parseOptions: { arrayFormat: "index" },
        stringifyOptions: { arrayFormat: "index" },
    });

    const {
        name,
        city,
        country,
        countryCode,
        preferences,
        tenantPointOfView,
        published,
    } = urlState;

    const parsedUrlState: ThemeQueryFilters = useMemo(() => {
        const nameSearchTerm = isString(name) ? name : undefined;
        const location = isTypeOf(
            {
                city: city || null,
                country,
                countryCode,
            },
            ThemeLocation,
        )
            ? {
                  city,
                  country,
                  countryCode,
              }
            : undefined;
        const parsedPreferences = isTypeOf(preferences, z.array(ThemePreference))
            ? preferences
            : undefined;
        const parsedPublished = isTypeOf(published, VisibilityFilter)
            ? published
            : undefined;
        const parsedTenantPointOfView = isTypeOf(
            tenantPointOfView,
            TenantPointOfViewSchema,
        )
            ? tenantPointOfView
            : undefined;
        return {
            nameSearchTerm,
            location,
            preferences: parsedPreferences,
            published: parsedPublished,
            tenantPointOfView: parsedTenantPointOfView,
        };
    }, [name, city, country, countryCode, preferences, published, tenantPointOfView]);

    return useMemo(
        () => ({
            urlState: parsedUrlState,
            setUrlState,
        }),
        [parsedUrlState, setUrlState],
    );
};

type AnyThemePreviewUrlQueryState = {
    memberId: unknown;
};

export const ThemePreviewUrlQuery = z.object({
    memberId: MemberId.optional(),
});
export type ThemePreviewUrlQuery = z.infer<typeof ThemePreviewUrlQuery>;

export const useThemePreviewUrlQueryState = () => {
    const [urlState, setUrlState] = useUrlState<AnyThemePreviewUrlQueryState>();

    const { memberId } = urlState;

    const parsedUrlState: ThemePreviewUrlQuery = useMemo(() => {
        const member = isString(memberId) ? memberId : undefined;
        return { memberId: member };
    }, [memberId]);

    return useMemo(
        () => ({
            urlState: parsedUrlState,
            setUrlState: (partialUrlState: Partial<ThemePreviewUrlQuery>) => {
                setUrlState({
                    memberId: partialUrlState.memberId,
                });
            },
        }),
        [parsedUrlState, setUrlState],
    );
};
